import { Raycaster, Vector2, type Camera, type Object3D } from "three/webgpu";
import type { EngineCallbacks } from "../core/engineTypes";
import type { CameraDirector } from "../core/CameraDirector";
import type { LabelPick } from "../core/LabelLayer";
import { INTERACTION_LAYER } from "./interactionLayer";

export type PickResult = {
  entityId: string;
  entityKind: string;
  parentId: string | null;
};

type PointerState = { x: number; y: number };

export class PointerController {
  private readonly raycaster = new Raycaster();
  private readonly pointer = new Vector2();
  private dragging = false;
  private dragDistance = 0;
  private lastX = 0;
  private lastY = 0;
  private screenX = 0;
  private screenY = 0;
  private hovered: string | null = null;
  private readonly activePointers = new Map<number, PointerState>();
  private pinchDistance = 0;
  private gestureConsumed = false;
  private navigationBroadcasted = false;
  private hoverFrame = 0;
  private pendingClientX = 0;
  private pendingClientY = 0;
  private pendingMouseParallax = false;

  constructor(
    private readonly element: HTMLCanvasElement,
    private readonly camera: Camera,
    private readonly cameraDirector: CameraDirector,
    private readonly callbacks: EngineCallbacks,
    private readonly interactiveProvider: () => Object3D[],
    private readonly labelPicker: (screenX: number, screenY: number) => LabelPick | null,
    private readonly visualSurfacePicker: (screenX: number, screenY: number) => PickResult | null,
  ) {
    this.raycaster.layers.set(INTERACTION_LAYER);
    element.addEventListener("pointerdown", this.onPointerDown);
    element.addEventListener("pointermove", this.onPointerMove);
    element.addEventListener("pointerup", this.onPointerUp);
    element.addEventListener("pointercancel", this.onPointerUp);
    element.addEventListener("dblclick", this.onDoubleClick);
    element.addEventListener("wheel", this.onWheel, { passive: false });
    window.addEventListener("keydown", this.onKeyDown);
    this.element.style.cursor = "grab";
  }

  private readonly onPointerDown = (event: PointerEvent) => {
    if (event.button !== 0) return;
    this.updatePointer(event);
    const startingGesture = this.activePointers.size === 0;
    const pointer = this.activePointers.get(event.pointerId);
    if (pointer) {
      pointer.x = event.clientX;
      pointer.y = event.clientY;
    } else {
      this.activePointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    }
    if (startingGesture) {
      this.dragging = true;
      this.dragDistance = 0;
      this.gestureConsumed = false;
      this.navigationBroadcasted = false;
      this.lastX = event.clientX;
      this.lastY = event.clientY;
    }
    if (this.activePointers.size >= 2) this.gestureConsumed = true;
    this.element.style.cursor = "grabbing";
    this.element.setPointerCapture?.(event.pointerId);
  };

  private readonly onPointerMove = (event: PointerEvent) => {
    const activePointer = this.activePointers.get(event.pointerId);
    if (activePointer) {
      activePointer.x = event.clientX;
      activePointer.y = event.clientY;
    }
    if (this.activePointers.size >= 2) {
      this.gestureConsumed = true;
      const pointers = this.activePointers.values();
      const a = pointers.next().value;
      const b = pointers.next().value;
      if (!a || !b) return;
      const distance = Math.hypot(a.x - b.x, a.y - b.y);
      if (this.pinchDistance > 0) {
        this.cameraDirector.zoomView((this.pinchDistance - distance) * 2.4);
        this.broadcastSceneNavigation();
      }
      this.pinchDistance = distance;
      return;
    }

    if (this.dragging) {
      const dx = event.clientX - this.lastX;
      const dy = event.clientY - this.lastY;
      this.dragDistance += Math.abs(dx) + Math.abs(dy);
      this.lastX = event.clientX;
      this.lastY = event.clientY;
      this.cameraDirector.rotateView(dx, dy);
      if (this.dragDistance > 10) this.broadcastSceneNavigation();
      return;
    }

    this.pendingClientX = event.clientX;
    this.pendingClientY = event.clientY;
    this.pendingMouseParallax = event.pointerType === "mouse";
    this.scheduleHoverUpdate();
  };

  private readonly onPointerUp = (event: PointerEvent) => {
    this.activePointers.delete(event.pointerId);
    if (this.activePointers.size < 2) this.pinchDistance = 0;
    if (this.element.hasPointerCapture?.(event.pointerId)) this.element.releasePointerCapture(event.pointerId);
    if (!this.dragging) return;
    if (this.activePointers.size > 0) {
      const remaining = this.activePointers.values().next().value;
      if (!remaining) return;
      this.lastX = remaining.x;
      this.lastY = remaining.y;
      return;
    }
    this.dragging = false;
    const tapThreshold = event.pointerType === "touch" ? 12 : 6;
    const cancelled = event.type === "pointercancel";
    if (!cancelled && !this.gestureConsumed && this.dragDistance < tapThreshold) {
      this.updatePointer(event);
      this.activateUnderPointer();
    }
    this.gestureConsumed = false;
    this.element.style.cursor = this.hovered ? "pointer" : "grab";
  };

  private readonly onDoubleClick = (event: MouseEvent) => {
    this.broadcastSceneNavigation();
    event.preventDefault();
    this.cameraDirector.resetView();
  };

  private readonly onWheel = (event: WheelEvent) => {
    this.cameraDirector.zoomView(event.deltaY);
    this.broadcastSceneNavigation();
    event.preventDefault();
  };

  private readonly onKeyDown = (event: KeyboardEvent) => {
    if (event.key === "ArrowLeft") this.cameraDirector.nudgeView(-0.08, 0);
    else if (event.key === "ArrowRight") this.cameraDirector.nudgeView(0.08, 0);
    else if (event.key === "ArrowUp") this.cameraDirector.nudgeView(0, -0.06);
    else if (event.key === "ArrowDown") this.cameraDirector.nudgeView(0, 0.06);
    else return;
    event.preventDefault();
  };

  private broadcastSceneNavigation() {
    if (this.navigationBroadcasted) return;
    this.navigationBroadcasted = true;
    window.dispatchEvent(new CustomEvent("knexus:scene-navigation"));
  }

  private updatePointer(event: PointerEvent | MouseEvent) {
    this.updatePointerFromClient(event.clientX, event.clientY);
  }

  private updatePointerFromClient(clientX: number, clientY: number) {
    const rect = this.element.getBoundingClientRect();
    const localX = clientX - rect.left;
    const localY = clientY - rect.top;
    this.screenX = localX;
    this.screenY = localY;
    this.pointer.set((localX / Math.max(rect.width, 1)) * 2 - 1, -((localY / Math.max(rect.height, 1)) * 2 - 1));
  }

  private scheduleHoverUpdate() {
    if (this.hoverFrame) return;
    this.hoverFrame = requestAnimationFrame(() => {
      this.hoverFrame = 0;
      if (this.dragging || this.activePointers.size > 0) return;
      this.updatePointerFromClient(this.pendingClientX, this.pendingClientY);
      if (this.pendingMouseParallax) this.cameraDirector.setParallax(this.pointer.x, this.pointer.y);
      this.updateHover();
    });
  }

  private updateHover() {
    const hit = this.pick();
    const id = hit?.entityId ?? null;
    if (id === this.hovered) return;
    this.hovered = id;
    this.element.style.cursor = id ? "pointer" : "grab";
    this.callbacks.onHover(id);
  }

  private activateUnderPointer() {
    const hit = this.pick();
    if (!hit?.entityId) return;
    if (hit.entityKind === "orbital") {
      if (hit.parentId) this.callbacks.onOrbitalActivate(hit.parentId, hit.entityId);
      return;
    }
    this.callbacks.onNodeActivate(hit.entityId);
  }

  private pick() {
    const labelHit = this.labelPicker(this.screenX, this.screenY);
    if (labelHit) return labelHit;
    const surfaceHit = this.visualSurfacePicker(this.screenX, this.screenY);
    if (surfaceHit) return surfaceHit;
    const objectHit = this.pickInteractiveObject();
    if (!objectHit) return null;
    const entityId = typeof objectHit.userData.entityId === "string" ? objectHit.userData.entityId : null;
    if (!entityId) return null;
    return {
      entityId,
      entityKind: typeof objectHit.userData.entityKind === "string" ? objectHit.userData.entityKind : "node",
      parentId: typeof objectHit.userData.parentId === "string" ? objectHit.userData.parentId : null,
    } satisfies PickResult;
  }

  private pickInteractiveObject() {
    this.raycaster.setFromCamera(this.pointer, this.camera);
    const intersections = this.raycaster.intersectObjects(this.interactiveProvider(), false);
    for (const intersection of intersections) {
      if (intersection.object.userData.pickEnabled !== false) return intersection.object;
    }
    return null;
  }

  dispose() {
    if (this.hoverFrame) cancelAnimationFrame(this.hoverFrame);
    this.element.removeEventListener("pointerdown", this.onPointerDown);
    this.element.removeEventListener("pointermove", this.onPointerMove);
    this.element.removeEventListener("pointerup", this.onPointerUp);
    this.element.removeEventListener("pointercancel", this.onPointerUp);
    this.element.removeEventListener("dblclick", this.onDoubleClick);
    this.element.removeEventListener("wheel", this.onWheel);
    window.removeEventListener("keydown", this.onKeyDown);
    this.callbacks.onHover(null);
  }
}
